import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Lock, Loader2, Pencil, RefreshCw, Rocket, MonitorPlay, FileStack,
  AlertTriangle, PlugZap, ExternalLink, Globe,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useSite } from "@/context/SiteContext";
import { withSession } from "@/context/AuthContext";
import { MeetingFinder } from "@/components/aa/MeetingFinder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FINDER_SLOT = '<div id="pv-meeting-finder-slot"></div>';

/**
 * The Emergent mockup viewer - FULL-BLEED. The mockup renders edge-to-edge
 * like a real site; the only overhead is one slim utility strip (page pills +
 * PDF + deploy when WP is connected). Mockup vs live status lives in the
 * TopBar chip.
 */
export default function SitePreviewPage() {
  const { activeSite, fetchHealth, previewToolsOpen } = useSite();
  const [pages, setPages] = useState(null);
  const [pagesError, setPagesError] = useState(false);
  const [slug, setSlug] = useState(null);
  const [page, setPage] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState(false);
  const [deploying, setDeploying] = useState(null);
  const [liveConfirm, setLiveConfirm] = useState(false);

  // Sticky mastheads inside the mockups pin below the app chrome via the
  // --pv-chrome-top CSS var. Measure the real chrome (top bar + page-details
  // strip when open) so the offset is exact on every breakpoint.
  const [chromeTop, setChromeTop] = useState(44);
  useEffect(() => {
    const measure = () => {
      const bar = document.querySelector('[data-testid="topbar"]');
      const strip = document.querySelector('[data-testid="preview-utility-strip"]');
      setChromeTop((bar?.offsetHeight || 44) + (strip?.offsetHeight || 0));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [previewToolsOpen]);

  const canvasRef = useRef(null);
  const pollRef = useRef(null);

  const siteId = activeSite?.id;
  const isSeed = !!activeSite?.is_seed;
  const wpConnected = !!activeSite?.wp_connected;
  // Maintenance/editor workspaces (already-live Flatsome client sites) render
  // the REAL production website in an iframe under the toolbar - no mockups.
  const liveMode = activeSite?.preview_mode === "live";

  // ---------- live-site iframe state
  const [livePath, setLivePath] = useState("/");
  const [proxyMode, setProxyMode] = useState(null); // null=checking, false=direct, true=backend proxy
  const [frameKey, setFrameKey] = useState(0);
  const [frameLoading, setFrameLoading] = useState(true);
  // Local-draft override: targets not yet pushed to WP render their stored
  // design in the same pane instead of a live-site 404.
  const [draftSrc, setDraftSrc] = useState(null);

  useEffect(() => {
    if (!liveMode || !siteId) return undefined;
    setLivePath("/");
    setDraftSrc(null);
    setProxyMode(null);
    setFrameLoading(true);
    let cancelled = false;
    axios
      .get(`${API}/live-preview/${siteId}/check`)
      .then(({ data }) => {
        if (cancelled) return;
        setProxyMode(data.frameable === false);
        if (data.frameable === false) {
          toast.info("Direct embed blocked by the live site - switched to the frame-safe proxy.", { duration: 4000 });
        }
      })
      .catch(() => {
        if (!cancelled) setProxyMode(false); // default: try direct embedding
      });
    return () => { cancelled = true; };
  }, [liveMode, siteId]);

  const loadPages = useCallback(async () => {
    if (!siteId || isSeed) return;
    setPagesError(false);
    setPages(null);
    try {
      const { data } = await axios.get(`${API}/sites/${siteId}/targets`);
      const list = data.targets || [];
      list.sort((a, b) => (a.slug === "home" ? -1 : b.slug === "home" ? 1 : 0));
      setPages(list);
      setSlug((cur) => (cur && list.some((p) => p.slug === cur) ? cur : list[0]?.slug || null));
    } catch (e) {
      setPagesError(true);
      setPages([]);
    }
  }, [siteId, isSeed]);

  useEffect(() => {
    setSlug(null);
    setPage(null);
    loadPages();
    return () => clearInterval(pollRef.current);
  }, [loadPages]);

  const loadPage = useCallback(async () => {
    if (!siteId || !slug || liveMode) return;
    setPageLoading(true);
    setPageError(false);
    try {
      const { data } = await axios.get(`${API}/preview/${siteId}/page/${slug}`);
      setPage(data);
    } catch (e) {
      setPageError(true);
      setPage(null);
    } finally {
      setPageLoading(false);
    }
  }, [siteId, slug, liveMode]);

  useEffect(() => { loadPage(); }, [loadPage]);

  const slugSet = useMemo(() => new Set((pages || []).map((p) => p.slug)), [pages]);
  const currentTarget = (pages || []).find((p) => p.slug === slug);

  const onCanvasClick = (e) => {
    const a = e.target.closest("a");
    if (!a) return;
    const href = a.getAttribute("href") || "";
    if (href.startsWith("tel:") || href.startsWith("mailto:") || href.startsWith("#")) return;
    e.preventDefault();
    if (/^https?:\/\//i.test(href)) {
      window.open(href, "_blank", "noopener");
      return;
    }
    const target = href.replace(/^\/+|\/+$/g, "") || "home";
    if (slugSet.has(target)) {
      setSlug(target);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      toast.info(`"/${target}/" isn't in this mockup yet — add it from Workspace Pages.`, { duration: 4000 });
    }
  };

  const deployPage = async (mode) => {
    if (!currentTarget) return;
    setLiveConfirm(false);
    setDeploying(mode);
    try {
      const { data } = await axios.post(`${API}/wp/deploy`, {
        target: currentTarget.id, mode, confirm: mode === "live", site_id: siteId,
      });
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const { data: job } = await axios.get(`${API}/wp/deploy/${data.job_id}`);
          if (job.status === "completed" || job.status === "failed") {
            clearInterval(pollRef.current);
            setDeploying(null);
            fetchHealth(siteId, true);
            if (job.status === "completed") {
              const link = job.result?.live_link || job.result?.preview_link;
              toast.success(`"${currentTarget.label}" deployed (${mode}).`, {
                action: link ? { label: "View", onClick: () => window.open(link, "_blank", "noopener") } : undefined,
              });
            } else {
              toast.error(`Deployment of "${currentTarget.label}" failed — see the Deploy Engine log.`);
            }
          }
        } catch (err) { /* keep polling */ }
      }, 2000);
    } catch (e) {
      setDeploying(null);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start the deployment.");
    }
  };

  // ---------- seed workspace: its Emergent version IS the React mirror
  if (isSeed) {
    return (
      <div data-testid="site-preview-page" className="mx-auto max-w-2xl px-5 pb-20 pt-28 text-center">
        <MonitorPlay size={36} className="mx-auto text-[#0050c0]" />
        <h1 className="mt-4 font-nunito text-xl font-extrabold text-[#383838]">
          {activeSite?.name} renders as the full React mirror
        </h1>
        <p className="mt-2 text-sm text-[#6e6e6e]">
          The seed site's Emergent version is the pixel-built app itself — every page, menu, and animation.
          Mockup workspaces preview here instead.
        </p>
        <Button asChild className="mt-5 rounded-[10px] bg-[#0050c0] text-white hover:bg-[#0343a0] font-nunito font-extrabold uppercase tracking-wide text-xs">
          <Link to="/" data-testid="preview-open-mirror-link">Open The Site</Link>
        </Button>
      </div>
    );
  }

  if (!activeSite) {
    return (
      <div data-testid="site-preview-page" className="px-5 pb-20 pt-24">
        <Skeleton className="h-10 w-72 rounded-[10px]" />
        <Skeleton className="mt-4 h-[480px] rounded-[14px]" />
      </div>
    );
  }

  const parts = page?.html ? page.html.split(FINDER_SLOT) : [];

  return (
    <div data-testid="site-preview-page">
      {/* Page details strip - hidden by default, toggled from the top bar's PAGE row */}
      {previewToolsOpen && (
      <div data-testid="preview-utility-strip" className="flex items-center gap-1.5 border-b border-black/10 bg-white px-2 py-1.5 sm:px-3">
        <div data-testid="preview-page-nav" className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
          {pages === null ? (
            <>
              <Skeleton className="h-6 w-16 rounded-full" />
              <Skeleton className="h-6 w-20 rounded-full" />
            </>
          ) : (
            pages.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`preview-page-pill-${p.slug}`}
                onClick={() => {
                  setSlug(p.slug);
                  if (liveMode) {
                    setFrameLoading(true);
                    if (p.local_only && !p.wp_id) {
                      // Not on the live site yet - show the stored draft design.
                      setDraftSrc(withSession(`${API}/preview/${siteId}/render/${p.slug}`));
                    } else {
                      setDraftSrc(null);
                      setLivePath(p.slug === "home" ? "/" : `/${p.slug}/`);
                    }
                  }
                }}
                className={`shrink-0 rounded-full border px-2.5 py-1 font-nunito text-[10px] font-extrabold transition-colors duration-150 ${
                  p.slug === slug
                    ? "border-transparent bg-[#383838] text-white"
                    : "border-black/15 bg-white text-[#6e6e6e] hover:border-black/40 hover:text-[#383838]"
                }`}
              >
                /{p.slug === "home" ? "" : p.slug + "/"}
                {liveMode && p.local_only && !p.wp_id && (
                  <span
                    data-testid={`preview-pill-draft-badge-${p.slug}`}
                    className={`ml-1 rounded-full px-1 py-px text-[8px] font-extrabold uppercase tracking-[0.08em] ${
                      p.slug === slug ? "bg-white/25 text-white" : "bg-[#fef2e5] text-[#c25a00]"
                    }`}
                  >
                    draft
                  </span>
                )}
              </button>
            ))
          )}
        </div>
        {currentTarget && (
          <Link
            to="/workspace"
            data-testid="preview-edit-link"
            aria-label="Edit in workspace"
            className="shrink-0 p-1 text-[#8a8a8a] transition-colors duration-150 hover:text-[#383838]"
          >
            <Pencil size={13} />
          </Link>
        )}
        <button
          type="button"
          data-testid="preview-refresh-button"
          onClick={() => {
            loadPages();
            if (liveMode) {
              setFrameLoading(true);
              setFrameKey((k) => k + 1);
            } else {
              loadPage();
            }
          }}
          aria-label="Refresh preview"
          className="shrink-0 p-1 text-[#8a8a8a] transition-colors duration-150 hover:text-[#383838]"
        >
          <RefreshCw size={13} />
        </button>
        {liveMode && (
          <a
            href={`https://${activeSite.production_domain}${livePath}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="preview-open-live-link"
            aria-label="Open the live site in a new tab"
            className="flex shrink-0 items-center gap-1 rounded-full border border-[#c9e4e1] bg-[#eef7f6] px-2 py-0.5 font-nunito text-[9px] font-extrabold uppercase tracking-[0.1em] text-[#2b6f68] transition-colors duration-150 hover:border-[#8fc9c3]"
          >
            <Globe size={10} />
            {activeSite.production_domain}
            <ExternalLink size={9} />
          </a>
        )}
        {wpConnected && currentTarget && (
          <>
            <Button
              type="button"
              size="sm"
              data-testid="preview-deploy-draft-button"
              disabled={!!deploying}
              onClick={() => deployPage("draft")}
              className="h-7 shrink-0 rounded-[8px] bg-[#f07000] px-2.5 font-nunito text-[9px] font-extrabold uppercase tracking-wide text-white hover:bg-[#d96400]"
            >
              {deploying === "draft" ? <Loader2 size={11} className="animate-spin" /> : <Rocket size={11} />}
              Draft
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="preview-deploy-live-button"
              disabled={!!deploying}
              onClick={() => setLiveConfirm(true)}
              className="h-7 shrink-0 rounded-[8px] border-black/20 px-2.5 font-nunito text-[9px] font-extrabold uppercase tracking-wide text-[#383838]"
            >
              {deploying === "live" ? <Loader2 size={11} className="animate-spin" /> : <ExternalLink size={11} />}
              Live
            </Button>
          </>
        )}
      </div>
      )}

      {/* Live-site maintenance mode: the REAL production website under the
          toolbar. Direct iframe first; frame-safe backend proxy fallback. */}
      {liveMode ? (
        <div
          data-testid="live-preview-frame-wrap"
          className="relative bg-white"
          style={{ height: `calc(100vh - ${chromeTop}px)` }}
        >
          {(proxyMode === null || frameLoading) && (
            <div
              data-testid="live-preview-loading"
              className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/90"
            >
              <Loader2 size={22} className="animate-spin text-[#0050c0]" />
              <p className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8a8a8a]">
                Loading {activeSite.production_domain}…
              </p>
            </div>
          )}
          {proxyMode !== null && (
            <iframe
              key={`${frameKey}-${proxyMode ? "proxy" : "direct"}-${draftSrc || livePath}`}
              data-testid="live-preview-iframe"
              title={`${activeSite.name} — live site`}
              src={
                draftSrc
                  || (proxyMode
                    ? withSession(`${API}/live-preview/${siteId}/proxy?path=${encodeURIComponent(livePath)}`)
                    : `https://${activeSite.production_domain}${livePath}`)
              }
              className="h-full w-full border-0"
              onLoad={() => setFrameLoading(false)}
            />
          )}
        </div>
      ) : pages !== null && pages.length === 0 ? (
        <div data-testid="preview-empty-state" className="flex flex-col items-center gap-3 px-6 py-24 text-center">
          <FileStack size={30} className="text-black/20" />
          <p className="font-nunito text-[15px] font-extrabold text-[#383838]">This mockup has no pages yet</p>
          <p className="max-w-md text-[13px] text-[#6e6e6e]">
            Stamp a brand template or import a live site from the workspace — pages appear here instantly, no WordPress needed.
          </p>
          <Button asChild className="mt-1 rounded-[10px] bg-[#0050c0] text-white hover:bg-[#0343a0] font-nunito font-extrabold uppercase tracking-wide text-xs">
            <Link to="/workspace">Open Workspace Pages</Link>
          </Button>
        </div>
      ) : pageLoading || (pages === null && !pagesError) ? (
        <div className="space-y-4 p-6">
          <Skeleton className="h-72 rounded-[10px]" />
          <Skeleton className="h-8 w-2/3 rounded-[8px]" />
          <Skeleton className="h-8 w-1/2 rounded-[8px]" />
          <Skeleton className="h-44 rounded-[10px]" />
        </div>
      ) : pageError || pagesError ? (
        <div data-testid="preview-error-state" className="flex flex-col items-center gap-3 px-6 py-24 text-center">
          <AlertTriangle size={28} className="text-amber-500" />
          <p className="font-nunito text-[15px] font-extrabold text-[#383838]">Couldn't render this page</p>
          <Button type="button" data-testid="preview-retry-button" onClick={() => { loadPages(); loadPage(); }} variant="outline" className="rounded-[10px] border-black/20 font-nunito text-xs font-extrabold">
            <RefreshCw size={13} /> Try Again
          </Button>
        </div>
      ) : page ? (
        <div ref={canvasRef} onClick={onCanvasClick} className="pv-canvas" data-testid="preview-canvas" style={{ "--pv-chrome-top": `${chromeTop}px` }}>
          {parts.length > 1 ? (
            <>
              <div dangerouslySetInnerHTML={{ __html: parts[0] }} />
              <div className="mx-auto max-w-[1110px] px-5 pb-10">
                <MeetingFinder />
              </div>
              <div dangerouslySetInnerHTML={{ __html: parts.slice(1).join("") }} />
            </>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: page.html }} />
          )}
        </div>
      ) : null}

      {/* Live deploy confirmation */}
      <AlertDialog open={liveConfirm} onOpenChange={setLiveConfirm}>
        <AlertDialogContent data-testid="preview-live-confirm-dialog" className="rounded-[14px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Push "{currentTarget?.label}" live?</AlertDialogTitle>
            <AlertDialogDescription>
              This overwrites /{slug}/ on {activeSite.production_domain} immediately. A revision is kept on
              WordPress so you can roll back from the deploy history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="preview-live-cancel" className="rounded-[10px]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="preview-live-confirm" onClick={() => deployPage("live")} className="rounded-[10px] bg-[#0050c0] text-white hover:bg-[#0343a0]">
              Deploy Live
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
